//import $ from "jquery";
import { Suspension, pyRuntimeError } from "@Sk";
import {
    currentBreakpoint,
    currentStack,
    debuggerRequest,
    isPausedOnServer,
    isServerRequestSuspension,
    modifyOutgoingServerCall,
    onDebuggerServerMessage,
    onServerCallResponse,
    sendBreakMessage,
    setBreakpoints,
    shouldSuspend,
} from "@platform-runner/debugger";
import PyDefUtils from "PyDefUtils";
import { setOnDebuggerMessage } from "@runtime/modules/_server/handlers";
import { setModifyOutgoingServerCall, setOnServerCallResponse } from "@runtime/modules/_server/rpc";
import { data } from "@runtime/runner/data";
import * as errorHandling from "@runtime/runner/error-handling";
import * as runner from "@runtime/runner/index";
import * as logging from "@runtime/runner/logging";
import { defer } from "@runtime/utils";
import { ExpandedFrameState } from "./shared-debugger-types";
import {onOpenedForm, setUpInstrumentation} from "@platform-runner/instrumentation";

function requestHeight() {
    runner.hardResize();
}

const {
    builtin: {
        func: pyFunc,
        none: { none$: pyNone },
        str: pyStr,
        exec: pyExec,
        print: pyPrint,
        KeyboardInterrupt,
    },
    builtins: pyBuiltins,
    misceval: { objectRepr, asyncToPromise: suspensionToPromise },
    ffi: { toPy },
} = Sk;

let globals: { [name: string]: any };
let clearRepl = false;
let stopExecution = false;
const SEEN = Symbol("seen");
const TIMEOUT = 90;
const YIELDLIMIT = 500;

function replInterrupt() {
    // todo should be able to stop a process from the repl
    // will need to do Sk.killableWhile = true;
    stopExecution = true;
}

function checkInterrupt() {
    if (stopExecution) {
        // if we're in a sleep then we also need to make sure the sleep
        // has a chance to stop its execution - use a setTimeout larger than TIMEOUT
        // note that this error might get raised twice but that's better than an infinite repl loop
        setTimeout(() => (stopExecution = false), TIMEOUT * 2);
        throw new KeyboardInterrupt();
    }
}

function clearReplFn() {
    clearRepl = true;
    return pyNone;
}

function setTimeoutOverride(fn: () => void, delay: number) {
    const interval = setInterval(() => {
        if (stopExecution) {
            clearInterval(interval);
            clearTimeout(timeout);
            // we have to call this fn because
            // it's the only way to resolve from the sleep's promise
            // this will check the suspension handlers after the sleep
            // if stopExecution is True then the promise will reject
            // (yes it's a hack)
            fn();
        }
    }, Math.min(delay / 4, TIMEOUT));
    const timeout = setTimeout(() => {
        clearInterval(interval);
        fn();
    }, delay);
}

let flushOutput = (when: null | number = null) => {};

function setReplStdOut() {
    // override Sk.output to batch print statements
    const oldOutput = Sk.output;
    let accumulatedPrints: null | string = null;
    let outputTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    let lastOut: number | null;
    flushOutput = (when = null) => {
        clearTimeout(outputTimeout);
        if (!stopExecution && accumulatedPrints !== null) {
            oldOutput(accumulatedPrints);
        }
        accumulatedPrints = null;
        lastOut = when;
    };

    Sk.output = (out, fromServer) => {
        if (stopExecution) {
            flushOutput();
            return;
        }
        if (fromServer) {
            flushOutput();
            oldOutput(out, fromServer);
            return;
        }
        const now = Date.now();
        accumulatedPrints ??= "";
        accumulatedPrints += out;
        if (!lastOut) {
            // always print the first entry
            flushOutput(now);
        } else if (now - lastOut > TIMEOUT) {
            // we could be stuck in a loop so do stdout
            flushOutput(now);
        } else {
            clearTimeout(outputTimeout);
            outputTimeout = setTimeout(flushOutput, TIMEOUT / 4);
        }
    };

    const oldOnerror = window.onerror;

    function clearBatchedOnerror(...args: Parameters<typeof window.onerror>) {
        flushOutput();
        oldOnerror(...args);
    }

    // @ts-ignore
    window.onerror = clearBatchedOnerror;
}

function setupGlobals() {
    if (globals !== undefined) {
        return globals;
    }
    const anvil = Sk.sysmodules.quick$lookup(new pyStr("anvil"));
    const clear = new pyFunc(clearReplFn);
    globals = {
        __file__: toPy("<repl>"),
        __name__: toPy("repl"),
        clear,
        __package__: new pyStr(window.anvilAppMainPackage),
    };
    if (anvil) {
        Object.assign(globals, { anvil, ...anvil.$d });
    }
    return globals;
}

function parseReplCode(code: string) {
    let compiledCode = code.trimEnd() || "None"; // always have a last line
    let hasReturn = false;
    try {
        const parse = Sk.parse("<repl>", compiledCode);
        const ast = Sk.astFromParse(parse.cst, "?", parse.flags);
        const lastExpr = ast.body[ast.body.length - 1];
        if (lastExpr instanceof Sk.astnodes.Expr) {
            const lines = compiledCode.split("\n");
            const lineWithLastExpr = lines[lastExpr.lineno - 1];
            const colOffset = lastExpr.col_offset;
            // technically this should be __builtins__._ but skulpt doesn't really have __builtins__ so improvise
            lines[lastExpr.lineno - 1] =
                lineWithLastExpr.slice(0, colOffset) + "__last_ret__ = " + lineWithLastExpr.slice(colOffset);
            compiledCode = lines.join("\n");
            hasReturn = true;
        }
    } catch {}
    return [compiledCode, hasReturn] as const;
}

async function withReplOverrides(fn: () => Promise<{ clearRepl: boolean } | undefined>) {
    stopExecution = false;
    const oldTimeout = Sk.setTimeout;
    Sk.lastYield = Sk.execStart = Date.now();
    Sk.setTimeout = setTimeoutOverride;
    try {
        return await fn();
    } catch (e) {
        flushOutput();
        throw e;
    } finally {
        Sk.setTimeout = oldTimeout;
    }
}

const SUSP_HANDLERS = {
    "*": () => checkInterrupt(),
    "Sk.debug": (r: Suspension<any>) => {
        checkInterrupt();
        if (currentBreakpoint) {
            // if there is a currently executing breakpoint then don't pause
            return;
        }
        let p = PyDefUtils.suspensionHandlers["Sk.debug"]?.(r);
        if (p && currentBreakpoint) {
            // tell the IDE that this command has "completed"
            currentExecution?.resolve(null);
        }
        return p;
    },
    "Sk.promise": (r: Suspension<any>) => {
        checkInterrupt();
        // check for a keyboardInterrupt before and after the promise
        // this is basically for sleeps where we may have interrupted while we were sleeping
        if (!r.data[SEEN]) {
            r.data[SEEN] = true;
            r.data.promise
                .then((res: any) => {
                    checkInterrupt();
                    return res;
                })
                .catch(() => {
                    // ignore
                });
        }
        const rv = PyDefUtils.suspensionHandlers["Sk.promise"]?.(r);
        if (isPausedOnServer && isServerRequestSuspension(r)) {
            r.data.request.abort(new pyRuntimeError("Cannot execute a server call whilst paused on the server"));
        }
        return rv;
    },
};

function replFinalize(hasReturn: boolean, globals: Record<string, any>) {
    if (hasReturn && globals.__last_ret__ !== pyNone && globals.__last_ret__ !== undefined) {
        // undefined to mitigate against py functions that forgot to return pyNone
        pyBuiltins._ = globals.__last_ret__;
        pyPrint([objectRepr(pyBuiltins._)]);
    }
    if (clearRepl) {
        clearRepl = false;
        return { clearRepl: true };
    }
}

var currentExecution = defer();

function replCommand([code, { expandedFrame }]: [string, { expandedFrame?: ExpandedFrameState }]) {
    let gbl = undefined,
        loc = undefined;
    if (expandedFrame !== undefined) {
        const frameId = expandedFrame.id;
        for (const stack of currentStack ?? []) {
            if (stack.id === frameId) {
                gbl = stack.$gbl;
                loc = stack.$tmp;
                break;
            }
        }
    }
    if (gbl === undefined) {
        gbl = setupGlobals();
    }
    const [compiledCode, hasReturn] = parseReplCode(code);

    const runReplCommand = () => {
        currentExecution = defer();

        suspensionToPromise(() => pyExec(compiledCode, gbl, loc), SUSP_HANDLERS).then(
            () => {
                if (currentBreakpoint) {
                    // TODO - this only works if we are in a client paused state
                    // if we're on the server, then actually we need to re-send the server debuggers
                    // along with the suspension that was paused
                    // but we don't hold onto that information
                    sendBreakMessage(null, currentBreakpoint.suspension, expandedFrame);
                }
                currentExecution.resolve(replFinalize(hasReturn, loc ?? gbl));
            },
            (err) => {
                currentExecution.reject(err);
            }
        );

        return currentExecution.promise;
    };

    return withReplOverrides(runReplCommand);
}

function appChanged() {
    const banner = document.createElement("div");
    banner.style.cssText = `
background: #ffd;
color: #333;
text-align: center;
border-bottom: 1px solid rgba(0, 0, 0, 0.2);
position: absolute;
left: 0;
top: 0;
width: 100%;
z-index: 100;
display: inline-flex;
justify-content: center;
`;
    banner.textContent = "The app has changed since you ran it.\u00a0";
    document.body.prepend(banner);
    const link = document.createElement("div");
    link.setAttribute("role", "button");
    link.style.color = "#1BB0EE";
    link.style.cursor = "pointer";
    link.textContent = "Click here to restart";
    banner.appendChild(link);
    banner.appendChild(document.createTextNode("."));
    link.onclick = () => postToIde({ fn: "restartApp" });
    const closeLink = document.createElement("div");
    closeLink.setAttribute("role", "button");
    closeLink.style.position = "absolute";
    closeLink.style.right = "10px";
    closeLink.style.fontWeight = "bold";
    closeLink.style.cursor = "pointer";
    closeLink.textContent = "✕";
    closeLink.onclick = () => banner.remove();
    banner.appendChild(closeLink);
}

//@ts-ignore
window.messages = {
    requestHeight,
    replCommand,
    replInterrupt,
    appChanged,
    locationReload,
    debuggerRequest,
    setBreakpoints,
};

//@ts-ignore
require("@runtime/messages");

function locationReload(...args: Parameters<typeof window.location.replace>) {
    window.location.replace(...args);
}

errorHandling.setHooks({
    onPythonException: (errorMsg) => {
        postToIde(errorMsg);
    },
    onRuntimeError: (errormsg, url, line, col, errorObj) =>
        // Target origin is the Anvil IDE
        postToIde({
            error: "Runtime error",
            fn: "runtimeError",
            msg: errormsg,
            url,
            line,
            col,
            stackTrace: errorObj && errorObj.stack,
        }),
    onUncaughtRuntimeError: (err) => {
        const sessionToken = window.anvilSessionToken;
        $.post(data.appOrigin + "/_/log?_anvil_session=" + sessionToken, {
            eventType: "runtimeError",
            event: err,
        });
    },
});

const postToIde = (msg: any) => {
    let ideWindow;
    if (window.parent !== window) {
        ideWindow = window.parent;
    } else if (window.opener) {
        ideWindow = window.opener;
    }
    if (!ideWindow) return;
    try {
        ideWindow.postMessage(msg, data.serverParams.ideOrigin);
    } catch (e) {}
};

logging.setHooks({
    onStdout: (text, fromServer) => {
        postToIde({ fn: "stdout", text, fromServer });
    },
    onUpdateReplState: (replStateUpdate) => {
        postToIde({ fn: "replStateUpdate", replStateUpdate });
    },
});

setOnDebuggerMessage(onDebuggerServerMessage);
setOnServerCallResponse(onServerCallResponse);
setModifyOutgoingServerCall(modifyOutgoingServerCall);

runner.setHooks({
    // Skulpt only uses `breakpoints` if `debugging` is true, so we can set it unconditionally.
    getSkulptOptions: () => ({ debugging: !!data.serverParams["inIDE"], breakpoints: shouldSuspend }),
    beforeLoadApp: async () => {
        if (data.serverParams["inIDE"]) {
            try {
                // @ts-ignore
                const bps = await window.anvilCallIdeFn("getBreakpoints", null, 2000);
                setBreakpoints(bps);
            } catch (e) {
                console.warn("Failed to get breakpoints", e);
            }
        }
        setUpInstrumentation();
    },
    onLoadedApp: () => {
        if (data.serverParams["inIDE"]) {
            const oldTitle = document.title;
            document.title = "Debug: " + oldTitle;

            if (window.parent !== window) {
                // then we're in an iframe
                // if we're in a new tab then leave the error-indicator as is
                $("#error-indicator").remove();
            }
            $("#session-expired-modal button.refresh").remove();

            postToIde({ fn: "newAppSession", sessionToken: window.anvilSessionToken });
            // batch print satements for better logging performance in the IDE
            setReplStdOut();
            // prevent infinite loops breaking the editor
            Sk.yieldLimit = YIELDLIMIT;
        } else {
            const isTableau = data.app.services?.find((s) => s.source === "/runtime/services/anvil/tableau.yml")
                ? true
                : undefined;
            $.post(window.anvilAppOrigin + "/_/log?_anvil_session=" + window.anvilSessionToken, {
                eventType: "runApp",
                event: {
                    hostname: window.location.hostname,
                    isTableau,
                },
            });
        }
    },
    onOpenedForm,
});
